import { Container, Graphics, type Renderer } from 'pixi.js';

/**
 * The day/night wash over the county.
 *
 * A full-screen multiply tint driven by the same clock the simulation runs its
 * routines on: a dark cool blue at night, a warm low sun at dawn and dusk, clear
 * at midday. This is not decoration -- it is the county's clock made visible, so
 * three in the morning looks like three in the morning. And the point it makes
 * is the quiet one the whole game makes: the light changes, the network does
 * not. A plate reads the same at 3 a.m. under a dead-black sky as it does at
 * noon, because an ALPR camera does not need the sun.
 *
 * Screen space, topmost layer, non-interactive. The HTML console sits above the
 * canvas and is untouched; only the map and the things on it take the light.
 */

// Keyframes across one day as [fraction 0..1, multiply tint]. Fraction 0 is
// midnight, 0.25 ~6 a.m., 0.5 noon, 0.75 ~6 p.m. The tint multiplies the scene,
// so 0xffffff is "full daylight, no change" and darker/cooler values dim it.
const KEYS: readonly (readonly [number, number])[] = [
  [0.0, 0x1a2340], // midnight -- deep cool blue, roads only just legible
  [0.22, 0x1f2740], // the dead hours
  [0.28, 0x494257], // first grey light, cool
  [0.32, 0xb08464], // sunrise -- warm, low
  [0.4, 0xdcd4c4], // morning, warming to full
  [0.5, 0xffffff], // noon -- clear
  [0.66, 0xf4e6cc], // afternoon, gold
  [0.74, 0xc07848], // sunset -- warm orange, low
  [0.8, 0x6a5560], // dusk, cooling
  [0.88, 0x28304a], // night falls
  [1.0, 0x1a2340], // wrap back to midnight
];

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

function tintAt(fraction: number): number {
  for (let i = 0; i < KEYS.length - 1; i++) {
    const [f0, c0] = KEYS[i]!;
    const [f1, c1] = KEYS[i + 1]!;
    if (fraction >= f0 && fraction <= f1) {
      const t = f1 === f0 ? 0 : (fraction - f0) / (f1 - f0);
      return lerpColor(c0, c1, t);
    }
  }
  return 0xffffff;
}

export class DayNight {
  readonly container = new Container();
  private readonly wash = new Graphics();
  private lastColor = -1;
  private lastW = 0;
  private lastH = 0;

  constructor(_renderer: Renderer) {
    this.wash.blendMode = 'multiply';
    this.container.addChild(this.wash);
    this.container.eventMode = 'none';
  }

  /** Redraw the wash for the current county time. Cheap: only on a real change. */
  update(timeSeconds: number, daySeconds: number, width: number, height: number): void {
    if (!(daySeconds > 0) || !Number.isFinite(timeSeconds)) return;
    const fraction = (((timeSeconds % daySeconds) + daySeconds) % daySeconds) / daySeconds;
    const color = tintAt(fraction);
    if (color === this.lastColor && width === this.lastW && height === this.lastH) return;
    this.lastColor = color;
    this.lastW = width;
    this.lastH = height;
    this.wash.clear();
    this.wash.rect(0, 0, width, height).fill({ color, alpha: 1 });
  }
}
